import { Link, makeStyles, tokens } from '@fluentui/react-components';
import {
  ArrowTrendingLines24Regular,
  Building16Regular,
  Call16Regular,
  Gift24Regular,
  Globe16Regular,
  Location16Regular,
  Mail16Regular,
  Target24Regular,
  Trophy24Regular,
} from '@fluentui/react-icons';
import { QueryAggregate, Routes, readColumnRawValue, toZonedWallClock, type TableRecord } from '@powerportalspro/core';
import {
  AuthStatus,
  LookupRecordContext,
  RecordContext,
  useAuth,
  useRecordContext,
  useT,
  useUserTimeZone,
} from '@powerportalspro/react';
import { Calendar, NewAppointmentCalendarButton } from '@powerportalspro/react-calendar';
import { AggregateDataverseChartDataSource, ChartType } from '@powerportalspro/react-charts';
import {
  ActivateRecordButton,
  AddressDisplayValue,
  ColumnDisplayValue,
  ColumnEdit,
  DataverseChart,
  DeactivateRecordButton,
  DeleteContextButton,
  DescriptionLocation,
  ExportGridButton,
  FileEdit,
  GridToolbarItems,
  ImageEdit,
  MenuBar,
  NavigateNewRecordGridButton,
  NavigateOpenRecordGridButton,
  RefreshContextButton,
  SaveContextButton,
  Section,
  SectionColumn,
  SubGrid,
  SummaryCard,
  SummaryCards,
  TabPanel,
  Tabs,
  Timeline,
} from '@powerportalspro/react-fluent';
import { useMemo, useState, type ReactElement } from 'react';
import {
  HeroFactText,
  heroBand,
  initials,
  RecordDocuments,
  recordContextTarget,
  type DetailPageProps,
  type HeroFactKind,
} from './DetailPageParts';
import {
  CLOSED_OPPORTUNITIES_FETCH_XML,
  CLOSED_OPPORTUNITIES_VIEW_ID,
  OPEN_OPPORTUNITIES_FETCH_XML,
  OPEN_OPPORTUNITIES_VIEW_ID,
  OPPORTUNITIES_VIEW_ID,
  WON_OPPORTUNITIES_FETCH_XML,
  getCalendarViews,
  getContactDocumentsView,
  getClosedOpportunitiesView,
  getOpenOpportunitiesView,
  getOpportunitiesView,
} from './DetailPageRecords';
import { OpportunitiesWonByMonthChart } from './OpportunitiesWonByMonthChart';

// The contact page the grids open. One person, the whole picture on one page: a header with their
// photo, name and job title, and where to reach them, drawn from the record the RecordContext
// loaded; four figures across the top — open pipeline, won revenue and win rate, each a SummaryCard
// over the deals they are the contact for, and their birthday from the record itself (the pipeline
// card opens the opportunities tab); Activate and Deactivate beside Save; and tabs for an overview
// with their account (read through a LookupRecordContext and drawn from display values) and two
// charts, their opportunities in three views — open, closed, and all of them grouped by status —
// with totals, their notes and portal messages in a Timeline, their appointments in a Calendar,
// their documents in a FileGrid to preview and download, and the rest of their details, their
// contract and profile picture among them.
//
// A new contact (no id in the address) shows only their fields: the header, the figures and the
// related records come once they are saved. The opportunities grid's Open and New buttons find the
// opportunity page registered with AddRecordPage, so they name no address.
//
// The documents are the ppp_file rows whose Contact lookup points at this contact, drawn by the
// RecordDocuments the account page's are.

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.DetailPages.ContactDetail';

const ACCOUNT_URL = 'demos/account';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalL,
  },
  hero: {
    ...heroBand,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXL,
    padding: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalXXL}`,
  },
  photo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: '88px',
    height: '88px',
    boxSizing: 'border-box',
    borderRadius: tokens.borderRadiusCircular,
    border: `${tokens.strokeWidthThick} solid ${tokens.colorNeutralForegroundOnBrand}`,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorBrandForeground1,
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightSemibold,
    overflow: 'hidden',
    boxShadow: tokens.shadow4,
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  heroText: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXS,
    minWidth: 0,
  },
  eyebrow: {
    fontSize: tokens.fontSizeBase200,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    opacity: 0.85,
  },
  name: {
    margin: 0,
    fontSize: tokens.fontSizeHero800,
    lineHeight: tokens.lineHeightHero800,
    fontWeight: tokens.fontWeightSemibold,
  },
  title: {
    fontSize: tokens.fontSizeBase400,
    lineHeight: tokens.lineHeightBase400,
    opacity: 0.9,
  },
  facts: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    paddingTop: tokens.spacingVerticalXS,
  },
  fact: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXS,
    minWidth: 0,
    fontSize: tokens.fontSizeBase300,
    overflowWrap: 'anywhere',
    '& > svg': {
      flexShrink: 0,
    },
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalL,
  },
  // The contact's account, as a card beside the charts.
  account: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusXLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
    minWidth: 0,
  },
  accountEyebrow: {
    color: tokens.colorBrandForeground1,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  accountHeader: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalM,
  },
  accountLogo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: '56px',
    height: '56px',
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    overflow: 'hidden',
  },
  accountIdentity: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  accountName: {
    fontSize: tokens.fontSizeBase500,
    lineHeight: tokens.lineHeightBase500,
    fontWeight: tokens.fontWeightSemibold,
  },
  accountIndustry: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  accountDetails: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXS,
  },
  accountDetail: {
    display: 'flex',
    alignItems: 'flex-start',
    columnGap: tokens.spacingHorizontalS,
    minWidth: 0,
    color: tokens.colorNeutralForeground2,
    '& > svg': {
      flexShrink: 0,
      marginTop: tokens.spacingVerticalXS,
    },
  },
  // Two facts with their labels, side by side, on a tinted strip.
  accountFacts: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  charts: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: tokens.spacingHorizontalXL,
  },
});

/** The contact page: a fixed contact with `id`, or the one in the address with `queryParameterName`. */
export function ContactDetail(props: DetailPageProps) {
  return (
    <RecordContext table="contact" {...recordContextTarget(props)} forceSuccessfulValidationBeforeSave>
      <ContactPage />
    </RecordContext>
  );
}

function ContactPage() {
  const styles = useStyles();
  const t = useT();
  const { record } = useRecordContext();
  // A saved contact: one with records of their own to show. A new one has no id until saved.
  const contactId = record?.id;
  const [tab, setTab] = useState<string>();

  return (
    <div className={styles.root}>
      {contactId && (
        <>
          <ContactHero />
          <ContactFigures onOpenTab={setTab} />
        </>
      )}

      <MenuBar>
        <SaveContextButton />
        <RefreshContextButton />
        <DeleteContextButton />
        <ContactStatusButtons />
      </MenuBar>

      {/* The selected tab is kept in ?tab=. The form's tabs mount with the page, so it validates
          and saves them unopened; the grids' each load when their tab is first opened. */}
      <Tabs selectedTabQueryParameter="tab" {...(tab && { selectedTab: tab })} onSelectedTabChange={setTab}>
        <TabPanel value="overview" header={t(`${STRINGS_BASE}.tab-overview`)} lazyMount={false}>
          <div className={styles.panel}>
            {contactId && <ContactCharts key={contactId} contactId={contactId} />}
            <Section columnCount={3}>
              <SectionColumn headerText={t(`${STRINGS_BASE}.section-person`)}>
                <ColumnEdit columnName="firstname" />
                <ColumnEdit columnName="lastname" />
                <ColumnEdit columnName="jobtitle" />
                <ColumnEdit columnName="parentcustomerid" />
                <ColumnEdit columnName="birthdate" />
                <ColumnEdit columnName="ppp_age" />
              </SectionColumn>
              <SectionColumn headerText={t(`${STRINGS_BASE}.section-reach`)}>
                <ColumnEdit columnName="emailaddress1" />
                <ColumnEdit columnName="telephone1" />
                <ColumnEdit columnName="mobilephone" />
                <ColumnEdit columnName="preferredcontactmethodcode" />
              </SectionColumn>
              <SectionColumn headerText={t(`${STRINGS_BASE}.section-address`)}>
                <ColumnEdit columnName="address1_line1" />
                <ColumnEdit columnName="address1_city" />
                <ColumnEdit columnName="address1_stateorprovince" />
                <ColumnEdit columnName="address1_postalcode" />
                <ColumnEdit columnName="address1_country" />
              </SectionColumn>
            </Section>
          </div>
        </TabPanel>

        {/* Keyed by the contact, so another one starts their grids afresh. */}
        {contactId && (
          <>
            <TabPanel value="opportunities" header={t(`${STRINGS_BASE}.tab-opportunities`)}>
              <div className={styles.panel}>
                <ContactOpportunities key={contactId} />
              </div>
            </TabPanel>
            <TabPanel value="timeline" header={t(`${STRINGS_BASE}.tab-timeline`)}>
              <div className={styles.panel}>
                <Timeline key={contactId} allowFiltering allowSavedViews savedViewsKey="contact-detail-timeline" />
              </div>
            </TabPanel>
            <TabPanel value="calendar" header={t(`${STRINGS_BASE}.tab-calendar`)}>
              <div className={styles.panel}>
                <ContactCalendar key={contactId} contactId={contactId} />
              </div>
            </TabPanel>
            <TabPanel value="documents" header={t(`${STRINGS_BASE}.tab-documents`)}>
              <div className={styles.panel}>
                <ContactDocuments key={contactId} contactId={contactId} />
              </div>
            </TabPanel>
          </>
        )}

        {/* The form's own fields — the contract and the picture among them, beneath the rest — so
            the tab mounts with the page and the form saves them unopened. */}
        <TabPanel value="details" header={t(`${STRINGS_BASE}.tab-details`)} lazyMount={false}>
          <div className={styles.panel}>
            <Section columnCount={2}>
              <SectionColumn headerText={t(`${STRINGS_BASE}.section-description`)}>
                <ColumnEdit columnName="description" />
              </SectionColumn>
              <SectionColumn headerText={t(`${STRINGS_BASE}.section-preferences`)}>
                <ColumnEdit columnName="preferredappointmenttimecode" />
                <ColumnEdit columnName="donotemail" />
                <ColumnEdit columnName="donotbulkemail" />
                <ColumnEdit columnName="donotphone" />
              </SectionColumn>
              <SectionColumn headerText={t(`${STRINGS_BASE}.section-contract`)}>
                <FileEdit columnName="ppp_contract" />
              </SectionColumn>
              <SectionColumn headerText={t(`${STRINGS_BASE}.section-picture`)}>
                <ImageEdit columnName="ppp_profileimage" displayImage />
              </SectionColumn>
            </Section>
          </div>
        </TabPanel>
      </Tabs>
    </div>
  );
}

/**
 * Activate and Deactivate, which show only to a user who may write the contact: the ones they added,
 * and the one they are signed in as. That last isn't theirs to deactivate (the demo's server refuses
 * it), so it doesn't get them.
 */
function ContactStatusButtons() {
  const auth = useAuth();
  const { record } = useRecordContext();
  const userId = auth.status === AuthStatus.Authenticated ? auth.user.userId : undefined;
  if (userId && record?.id?.toLowerCase() === userId.toLowerCase()) return null;

  return (
    <>
      <ActivateRecordButton />
      <DeactivateRecordButton />
    </>
  );
}

// An image column's address, versioned by its timestamp so the browser keeps it until it changes;
// none when the record has no image.
function imageUrl(record: TableRecord | undefined, column: string, size: number): string | null {
  const version = readColumnRawValue<number | string>(record, `${column}_timestamp`);
  return record?.id && version != null
    ? Routes.api.getRetrieveImageRoute(record.tableName, record.id, column, version, size)
    : null;
}

// The contact's photo, name and title, and where to reach them, from the loaded record.
function ContactHero() {
  const styles = useStyles();
  const t = useT();
  const { record } = useRecordContext();
  const formatted = record?.formattedValues ?? {};
  const name = readColumnRawValue<string>(record, 'fullname') ?? '';
  const jobTitle = readColumnRawValue<string>(record, 'jobtitle');

  // The picture they uploaded, else the one Dataverse keeps for them.
  const photoUrl = imageUrl(record, 'ppp_profileimage', 160) ?? imageUrl(record, 'entityimage', 160);

  const facts: [ReactElement, string | null | undefined, HeroFactKind?][] = [
    [<Building16Regular key="account" />, formatted['parentcustomerid']],
    [<Location16Regular key="city" />, readColumnRawValue<string>(record, 'address1_city'), 'address'],
    [<Mail16Regular key="email" />, readColumnRawValue<string>(record, 'emailaddress1'), 'email'],
    [
      <Call16Regular key="phone" />,
      readColumnRawValue<string>(record, 'telephone1') ?? readColumnRawValue<string>(record, 'mobilephone'),
    ],
  ];

  return (
    <header className={styles.hero}>
      <div className={styles.photo}>
        {photoUrl ? <img className={styles.image} src={photoUrl} alt="" /> : <span>{initials(name)}</span>}
      </div>
      <div className={styles.heroText}>
        <span className={styles.eyebrow}>{t(`${STRINGS_BASE}.eyebrow`)}</span>
        <h2 className={styles.name}>{name}</h2>
        {jobTitle && <span className={styles.title}>{jobTitle}</span>}
        <div className={styles.facts}>
          {facts
            .filter(([, text]) => !!text)
            .map(([icon, text, kind]) => (
              <span key={icon.key} className={styles.fact}>
                {icon}
                <HeroFactText text={text!} kind={kind} mapTitle={name} />
              </span>
            ))}
        </div>
      </div>
    </header>
  );
}

// The figures across the top: three over the deals they are the contact for, which Dataverse works
// out and the server formats — the pipeline card opens the opportunities tab — and their birthday.
function ContactFigures({ onOpenTab }: { onOpenTab: (tab: string) => void }) {
  const t = useT();
  const { record } = useRecordContext();
  const timeZone = useUserTimeZone();
  const birthdate = readColumnRawValue<Date>(record, 'birthdate');
  const birthdayNote = birthdate ? birthdayNoteOf(birthdate, todayIn(timeZone), t) : null;

  return (
    <SummaryCards>
      <SummaryCard
        icon={<ArrowTrendingLines24Regular />}
        label={t(`${STRINGS_BASE}.kpi-pipeline`)}
        relationshipName="opportunity_parent_contact"
        fetchXml={OPEN_OPPORTUNITIES_FETCH_XML}
        columnName="estimatedvalue"
        aggregate={QueryAggregate.Sum}
        format="C0"
        caption={t(`${STRINGS_BASE}.open-deals`)}
        captionAggregate={QueryAggregate.Count}
        onClick={() => onOpenTab('opportunities')}
      />
      <SummaryCard
        icon={<Trophy24Regular />}
        label={t(`${STRINGS_BASE}.kpi-won`)}
        relationshipName="opportunity_parent_contact"
        fetchXml={WON_OPPORTUNITIES_FETCH_XML}
        columnName="actualvalue"
        aggregate={QueryAggregate.Sum}
        format="C0"
        caption={t(`${STRINGS_BASE}.won-deals`)}
        captionAggregate={QueryAggregate.Count}
      />
      <SummaryCard
        icon={<Target24Regular />}
        label={t(`${STRINGS_BASE}.kpi-win-rate`)}
        relationshipName="opportunity_parent_contact"
        fetchXml={WON_OPPORTUNITIES_FETCH_XML}
        ofFetchXml={CLOSED_OPPORTUNITIES_FETCH_XML}
        aggregate={QueryAggregate.Count}
      />
      <SummaryCard
        icon={<Gift24Regular />}
        label={t(`${STRINGS_BASE}.kpi-birthday`)}
        columnName="birthdate"
        {...(birthdayNote && { caption: birthdayNote })}
      />
    </SummaryCards>
  );
}

// Today in the user's time zone, which the dates the page shows are in — the browser's until the
// server has named it — as a UTC midnight.
function todayIn(timeZone: string | undefined): number {
  const now = timeZone ? toZonedWallClock(new Date(), timeZone) : new Date();
  return Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
}

// How far off their next birthday is, and the age they turn on it. A date-only column, so its
// day is read in UTC.
function birthdayNoteOf(birthdate: Date, today: number, t: ReturnType<typeof useT>): string {
  const now = new Date(today);
  // A birthday on February 29th falls on the 28th in other years.
  const birthdayIn = (year: number) => {
    const daysInMonth = new Date(Date.UTC(year, birthdate.getUTCMonth() + 1, 0)).getUTCDate();
    return Date.UTC(year, birthdate.getUTCMonth(), Math.min(birthdate.getUTCDate(), daysInMonth));
  };
  let year = now.getUTCFullYear();
  if (birthdayIn(year) < today) year++;

  const days = Math.round((birthdayIn(year) - today) / 86_400_000);
  return days === 0
    ? t(`${STRINGS_BASE}.birthday-today`)
    : t(`${STRINGS_BASE}.birthday-in`, [String(year - birthdate.getUTCFullYear()), String(days)]);
}

// The account's details under its name, each behind its icon; one it has no value for is left out.
const ACCOUNT_DETAILS: readonly { column: string; icon: ReactElement }[] = [
  { column: 'telephone1', icon: <Call16Regular /> },
  { column: 'websiteurl', icon: <Globe16Regular /> },
];

// The contact's company, read through their lookup to it: LookupRecordContext gives it a record
// context of its own, which the card reads, drawing it from display values.
function ContactAccount() {
  return (
    <LookupRecordContext columnName="parentcustomerid">
      <ContactAccountCard />
    </LookupRecordContext>
  );
}

function ContactAccountCard() {
  const styles = useStyles();
  const t = useT();
  const { record: account } = useRecordContext();
  // Drawn only for an account: a contact without one, or whose company is another contact, shows
  // no card.
  if (!account?.id || account.tableName !== 'account') return null;

  const name = readColumnRawValue<string>(account, 'name') ?? '';
  const logo = imageUrl(account, 'entityimage', 112);
  const hasAddress = ['address1_composite', 'address1_line1', 'address1_city'].some(
    (column) => readColumnRawValue(account, column) != null,
  );

  return (
    <section className={styles.account}>
      <span className={styles.accountEyebrow}>{t(`${STRINGS_BASE}.account`)}</span>
      <div className={styles.accountHeader}>
        <div className={styles.accountLogo}>
          {logo ? <img className={styles.image} src={logo} alt="" /> : <span>{initials(name)}</span>}
        </div>
        <div className={styles.accountIdentity}>
          <span className={styles.accountName}>{name}</span>
          <span className={styles.accountIndustry}>{account.formattedValues?.['industrycode']}</span>
        </div>
      </div>
      <div className={styles.accountDetails}>
        {ACCOUNT_DETAILS.filter(({ column }) => readColumnRawValue(account, column) != null).map(({ column, icon }) => (
          <div key={column} className={styles.accountDetail}>
            {icon}
            <ColumnDisplayValue columnName={column} displayLabelWhenAvailable={false} descriptionLocation={DescriptionLocation.None} />
          </div>
        ))}
        {hasAddress && (
          <div className={styles.accountDetail}>
            <Location16Regular />
            <AddressDisplayValue displayLabelWhenAvailable={false} descriptionLocation={DescriptionLocation.None} />
          </div>
        )}
      </div>
      <div className={styles.accountFacts}>
        <ColumnDisplayValue
          columnName="numberofemployees"
          label={t(`${STRINGS_BASE}.account-employees`)}
          descriptionLocation={DescriptionLocation.None}
        />
        <ColumnDisplayValue
          columnName="revenue"
          label={t(`${STRINGS_BASE}.account-revenue`)}
          descriptionLocation={DescriptionLocation.None}
        />
      </div>
      <Link href={`${ACCOUNT_URL}?accountId=${account.id}`}>{t(`${STRINGS_BASE}.account-open`)}</Link>
    </section>
  );
}

// Their account, their won revenue by month, and how many of their deals are open, won and lost.
function ContactCharts({ contactId }: { contactId: string }) {
  const styles = useStyles();
  const t = useT();
  const byStatus = useMemo(
    () =>
      new AggregateDataverseChartDataSource({
        tableName: 'opportunity',
        groupByColumn: 'statecode',
        aggregateColumn: 'opportunityid',
        aggregate: QueryAggregate.Count,
        filterXml: `<filter><condition attribute='parentcontactid' operator='eq' value='${contactId}' /></filter>`,
      }),
    [contactId],
  );

  return (
    <div className={styles.charts}>
      <ContactAccount />
      <OpportunitiesWonByMonthChart lookupColumn="parentcontactid" />
      <DataverseChart
        dataSource={byStatus}
        type={ChartType.Doughnut}
        title={t(`${STRINGS_BASE}.chart-by-status`)}
        height="320px"
      />
    </div>
  );
}

function ContactOpportunities() {
  const t = useT();
  const views = useMemo(
    () => [
      getOpenOpportunitiesView(t(`${STRINGS_BASE}.view-open-opportunities`)),
      getClosedOpportunitiesView(t(`${STRINGS_BASE}.view-closed-opportunities`)),
      getOpportunitiesView(t(`${STRINGS_BASE}.view-opportunities`)),
    ],
    [t],
  );
  return (
    <SubGrid
      tableName="opportunity"
      relationshipName="opportunity_parent_contact"
      maxHeight="520px"
      customViewDefinitions={views}
      viewIds={[OPEN_OPPORTUNITIES_VIEW_ID, CLOSED_OPPORTUNITIES_VIEW_ID, OPPORTUNITIES_VIEW_ID]}
      defaultViewId={OPPORTUNITIES_VIEW_ID}
      persistedStateQueryParameter="opportunitiesState"
    >
      <GridToolbarItems>
        <NavigateNewRecordGridButton />
        <NavigateOpenRecordGridButton />
        <ExportGridButton />
      </GridToolbarItems>
    </SubGrid>
  );
}

// The contact's appointments, tasks and phone calls.
function ContactCalendar({ contactId }: { contactId: string }) {
  const t = useT();
  const regarding = useMemo(() => ({ tableName: 'contact', id: contactId }), [contactId]);
  const views = useMemo(
    () => getCalendarViews(t(`${STRINGS_BASE}.view-activities`), t(`${STRINGS_BASE}.view-to-do`)),
    [t],
  );
  return (
    <Calendar
      regarding={regarding}
      defaultLayout="month"
      height="640px"
      customViewDefinitions={views}
      allowFiltering
      allowSavedViews
      savedViewsKey="contact-detail-calendar"
      persistedStateQueryParameter="calendarState"
      editable
    >
      <NewAppointmentCalendarButton />
    </Calendar>
  );
}

// The contact's documents: the files whose Contact lookup points at them.
function ContactDocuments({ contactId }: { contactId: string }) {
  const t = useT();
  const view = useMemo(
    () => getContactDocumentsView(contactId, t(`${STRINGS_BASE}.view-documents`)),
    [contactId, t],
  );
  return <RecordDocuments view={view} lookupColumn="ppp_contactid" table="contact" recordId={contactId} />;
}
